import get from 'lodash/get';
import { ORDER_TYPE_MANUAL, PAYMENT_METHODS } from '../../constants';
import { OrderAttendee } from '../../models/attendee';
import { PaymentOptionType } from '../../models/paymentMethods/enums';
import { CheckoutState } from '../../state';
import { isUserLoggedIn } from '../user';
import { getNamespaceValue } from '../utils/selector';

export const getIsNosSourceOfTruth = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'isNosSourceOfTruth', false);

export const getCurrencyFromOrder = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'costs.gross.currency');

export const getOrderId = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'id');

export const getOrderPersistentId = (state: CheckoutState) => {
    // Orders always have an ID. In LOS (Legacy Order Service) before the order is placed it is in a volatile state,
    // living in Redis. In this state the ID of the order is different than the ID after the order is placed.
    // We can distinguish both IDs because the volatile ID always begins with `O-` prefix.
    // In NOS (New Order Service) the Order Document always has the same ID, before it is confirmed and afterwards.
    return getNamespaceValue(state.order, 'isPlaced', false)
        ? getOrderId(state)
        : null;
};

export const getOrderRegistrationFormSurveyInfo = (state: CheckoutState) => {
    return getNamespaceValue(state, 'order.registrationForm.surveyInfo');
};

export const hasExistingOrder = (state: CheckoutState) =>
    !!getNamespaceValue(state.app, 'existingOrderId');

/**
 * @returns `true` when there is an exising order ID and it is a manual order
 */
export const isAddAttendeesOrder = (state: CheckoutState) => {
    return (
        hasExistingOrder(state) &&
        getNamespaceValue(state.order, 'augmentedInfo.orderType') ===
            ORDER_TYPE_MANUAL
    );
};

export const shouldManageUserSavedSurveyData = (state: CheckoutState) =>
    isUserLoggedIn(state) && !isAddAttendeesOrder(state);

export const hasOfflinePaymentMethods = (state: CheckoutState) =>
    !!getNamespaceValue(state.order, 'availablePaymentMethods')?.some(
        (paymentMethod) =>
            paymentMethod.type === PaymentOptionType.OFFLINE ||
            paymentMethod.instrumentTypes.some((instrument) =>
                [
                    PAYMENT_METHODS.BARCODE,
                    PAYMENT_METHODS.BOLETO_BANCARIO,
                ].includes(instrument.type),
            ),
    );

export const getOrderAttendeeById = (
    state: CheckoutState,
    attenderId: string,
) => {
    const orderAttendees = getNamespaceValue(
        state,
        'order.attendees',
    ) as any as OrderAttendee[];
    return orderAttendees.find(
        (attendee: OrderAttendee) => attendee.id === attenderId,
    );
};

export const getTimeRemaining = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'timeRemaining');

export const getOrderRedirectedPaymentTimeoutId = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'redirectedPaymentTimeoutId');

export const getRedirectUrlFromTicketBuyerSettings = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'ticketBuyerSettings.redirectUrl');

export const getRedirectUrlFromRedirectInstructions = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'redirectInstructions.url');

export const canRetrySubmit = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'canRetrySubmit');

export const getAttendees = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'attendees', []);

export const getAvailablePaymentMethods = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'availablePaymentMethods', []);

export const getOrderStatus = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'status');

export const getTaxIdName = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'taxIdName');

export const getHasExternalPaymentFirstCallCompleted = (state: CheckoutState) =>
    !!getNamespaceValue(state.order, 'hasExternalPaymentFirstCallCompleted');

export const getOrderType = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'augmentedInfo.orderType');

export const getDisplayInstallments = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'displayInstallments', null);

export const getTaxExemptionData = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'taxExemptionData');

export const getHasRetryPaymentError = (state: CheckoutState) =>
    getNamespaceValue(state.order, 'hasRetryPaymentError');

/**
 * Returns total gross major value of the order
 *
 * @param {*} state the application state
 */
export const getTotalCostsFromGrossMajorValue = (state: CheckoutState) =>
    get(state, 'order.costs.gross.majorValue', 0);

/**
 * Returns total gross value of the order
 *
 * @param {*} state the application state
 */
export const getTotalCostsFromGrossValue = (state: CheckoutState) =>
    get(state, 'order.costs.gross.value', 0);
